// Package dependencies
import Promise from 'promise';
import _ from 'lodash';

// Local Dependencies
import { fetchHelper } from './fetch-helper';

// default state cache time, 10 minutes
const CACHE_TTL = 600;

const bole = require('bole')('front-page');

const MAIN_POST_ID = process.env.PRESS_PAGE_ID;

let stateStoreTime = 0;

export const RECEIVE_ARTICLES = 'RECEIVE_ARTICLES';
export const RECEIVE_PAGE = 'RECEIVE_PAGE';
export const REQUEST_ERROR = 'REQUEST_ERROR';
export const RECEIVE_SPECIALISTS = 'RECEIVE_SPECIALISTS';
export const RECEIVE_FEATURES = 'RECEIVE_FEATURES';

export const ARTICLES_SUBTYPE = {
  HEADLINES: 'HEADLINES',
  MUST_READ: 'MUST_READ',
  PRESS_RELEASES: 'PRESS_RELEASES',
};

// Action creators
export const requestError = (error, url = '(url not provided)') => {
  bole.error(`Failed to fetch ${url}: `, error);
  return (dispatch) =>
    dispatch({
      type: REQUEST_ERROR,
      error: {
        url,
        error,
      },
    });
};

export function fetchWPPosts({
  dispatch,
  query,
  callback,
  fetchCallback,
  link,
}) {
  const params = _.assign(
    {
      _embed: true,
      _envelope: true,
    },
    query || {}
  );

  const fetchCallbackErr = (error) => {
    dispatch(requestError(error, link));

    if (callback) {
      callback();
    }
  };

  fetchHelper(
    link,
    undefined,
    params,
    'GET',
    (response) => {
      fetchCallback(response);

      if (callback) {
        callback();
      }
    },
    fetchCallbackErr
  );
}

export function fetchPressReleaseByPage({ callback, pressReleaseType, page }) {
  return (dispatch) => {
    fetchWPPosts({
      dispatch,
      query: {
        press_release_type: pressReleaseType,
        page: page + 1,
        featured: false,
      },
      callback,
      fetchCallback: (response) => {
        dispatch({
          type: RECEIVE_PAGE,
          subtype:
            pressReleaseType === 'internal'
              ? ARTICLES_SUBTYPE.PRESS_RELEASES
              : ARTICLES_SUBTYPE.HEADLINES,
          data: response,
          page,
        });
      },
      link: process.env.WP_API_PROXY_URL,
    });
  };
}

function fetchCompanyPages({
  callback,
  internal,
  featured,
  state,
  forceFetch,
}) {
  return (dispatch) => {
    if (
      !forceFetch &&
      !_.isEmpty(
        _.get(
          state,
          `pressArticles.${internal ? 'pressReleases' : 'headlines'}.${
            featured ? 'featured' : 'archived'
          }`
        )
      )
    ) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      query: {
        press_release_type: internal ? 'internal' : 'external',
        featured,
      },
      callback,
      fetchCallback: (response) => {
        dispatch({
          type: RECEIVE_ARTICLES,
          subtype: internal
            ? ARTICLES_SUBTYPE.PRESS_RELEASES
            : ARTICLES_SUBTYPE.HEADLINES,
          data: {
            [featured ? 'featured' : 'archived']: {
              data: response.body,
              headers: response.headers,
            },
          },
        });
      },
      link: `${process.env.WP_API_URL}/company-pages`,
    });
  };
}

export function fetchHeadlines(config) {
  return fetchCompanyPages(config);
}

export const receiveSpecialists = (data) => ({
  type: RECEIVE_SPECIALISTS,
  data,
});

export const receiveFeatures = (data) => ({
  type: RECEIVE_FEATURES,
  data,
});

export function fetchSpecialists({ callback, state, forceFetch }) {
  return (dispatch) => {
    if (!forceFetch && !_.isEmpty(_.get(state, 'specialistsData.data'))) {
      callback();
      return;
    }

    fetchWPPosts({
      dispatch,
      query: {},
      callback,
      fetchCallback: (response) => {
        dispatch(receiveSpecialists(response.body.nw_associated_users));
      },
      link: `${process.env.WP_API_URL}/company-pages/${MAIN_POST_ID}`,
    });
  };
}

export function bootstrapAppWithPromises({ promises, callback }) {
  return () => {
    const initApp = (/* dispatch, getState */) => {
      callback();
    };

    if (promises) {
      Promise.all(promises).done(initApp, (err) => {
        // In dev log the error.
        if (process.env.NODE_ENV === 'development') {
          bole.error('Error in bootstrap', err.stack);
        }

        // Call the callback anyway so it can render the error on the page.
        callback();
      });
    } else {
      initApp();
    }
  };
}

export function bootstrapApp(data) {
  return (dispatch, getState) => {
    const state = getState();
    let forceFetch = _.get(data, 'query.clearCache') === 'true';
    const promises = [];
    const now = Date.now();

    if (!stateStoreTime || (now - stateStoreTime) / 1000 > CACHE_TTL) {
      forceFetch = true;
      stateStoreTime = now;
    }

    const createPromise = ({ fetchFunction, featured, params, req }) => {
      promises.push(
        new Promise((callback) => {
          dispatch(
            fetchFunction({
              callback,
              featured,
              params,
              forceFetch,
              req,
              state,
            })
          );
        })
      );
    };

    switch (data.name) {
      case '/':
        break;

      case 'headlines':
        createPromise({ fetchFunction: fetchHeadlines, featured: true });
        createPromise({ fetchFunction: fetchHeadlines, featured: false });
        break;

      case 'press-page':
        createPromise({ fetchFunction: fetchHeadlines, featured: true });
        break;

      case 'specialists-page':
        createPromise({ fetchFunction: fetchSpecialists });
        break;

      default:
      // do nothing
    }

    dispatch(
      bootstrapAppWithPromises({
        promises,
        ...data,
      })
    );
  };
}
